import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";

const PROFILE_ACTIONS = [
  {
    value: "defer",
    label: "Defer to Outlet",
    description:
      "No profile-level gate. Requests from this reporter will be evaluated based on the outlet config only.",
  },
  { value: "manual_review", label: "Manual Review", description: "Route this person's requests to manual review" },
  { value: "ghost", label: "Ghost", description: "Auto-ghost all requests from this person" },
];

function MediaProfileConfigModal({ profileId, profileName, profileType, currentConfig, onSave, onClose }) {
  const currentAction = currentConfig ? currentConfig.auto_action : "defer";
  const [selectedAction, setSelectedAction] = useState(currentAction);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const headers = {
        ...authenticatedHeadersHash(),
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      };

      let configResponse;
      if (currentConfig?.id) {
        configResponse = await fetch(`/admin/api/request_approval_configs/${currentConfig.id}`, {
          method: "PATCH",
          headers,
          body: JSON.stringify({
            request_approval_config: { auto_action: selectedAction },
          }),
        });
      } else {
        configResponse = await fetch("/admin/api/request_approval_configs", {
          method: "POST",
          headers,
          body: JSON.stringify({
            request_approval_config: {
              configurable_type: profileType,
              configurable_id: profileId,
              auto_action: selectedAction,
            },
          }),
        });
      }

      if (!configResponse.ok) {
        const data = await configResponse.json();
        setError(data.errors?.join(", ") || "Failed to save config");
        return;
      }

      // Post reason as comment if provided
      if (reason.trim()) {
        await fetch("/admin/api/comments", {
          method: "POST",
          headers,
          body: JSON.stringify({
            comment: {
              commentable_type: profileType,
              commentable_id: profileId,
              body: reason.trim(),
            },
          }),
        });
      }

      const savedConfig = await configResponse.json();
      onSave(savedConfig);
    } catch (err) {
      setError("Network error - please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Set Profile Rule for {profileName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Profile rules can restrict (ghost or manual review) or defer to the outlet config. Approval is controlled at
          the outlet level.
        </p>

        {PROFILE_ACTIONS.map((action) => (
          <div className="form-check mb-2" key={action.value}>
            <input
              className="form-check-input"
              type="radio"
              name="profileAction"
              id={`profile-action-${action.value}`}
              value={action.value}
              checked={selectedAction === action.value}
              onChange={() => setSelectedAction(action.value)}
            />
            <label className="form-check-label" htmlFor={`profile-action-${action.value}`}>
              <strong>{action.label}</strong>
              <br />
              <small className="text-muted">{action.description}</small>
            </label>
          </div>
        ))}

        <div className="mt-3">
          <label htmlFor="profileReason" className="form-label text-muted small">
            Reason (optional)
          </label>
          <textarea
            id="profileReason"
            className="form-control form-control-sm"
            rows="2"
            placeholder="Why are you setting this rule?"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default MediaProfileConfigModal;
